import { useEffect, useState, useCallback } from "react";
import HttpClient from "../utils/HttpClient";

export default function useUserCountry({ autoFetch = true } = {}) {
    const [country, setCountry] = useState(null);
    const [region, setRegion] = useState(null);
    const [ip, setIp] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetcher can be called manually via refresh() or automatically on mount
    const fetchCountry = useCallback(async (opts = { force: false }) => {
        // Avoid concurrent runs
        if (loading && !opts.force) return;
        setLoading(true);
        setError(null);

        const controllers = [];
        try {
            // ensure we have an IP
            let ipToUse = ip;

            // try using cached IP from localStorage to avoid refetching on navigation
            const CACHE_KEY = "user_ip_cache";
            const THIRTY_MIN = 30 * 60 * 1000;

            if (!ipToUse) {
                try {
                    if (typeof window !== "undefined" && window.localStorage) {
                        const raw = window.localStorage.getItem(CACHE_KEY);
                        if (raw) {
                            const parsed = JSON.parse(raw);
                            if (parsed?.ip) {
                                ipToUse = parsed.ip;
                                setIp(ipToUse);

                                // If cache is older than 30 minutes, verify against API
                                const age = Date.now() - (parsed.ts || 0);
                                if (age >= THIRTY_MIN) {
                                    try {
                                        const ctrlVerify = new AbortController();
                                        controllers.push(ctrlVerify);
                                        const resVerify = await fetch("https://api.ipify.org?format=json", { signal: ctrlVerify.signal });
                                        if (resVerify.ok) {
                                            const verified = await resVerify.json();
                                            const fetchedIp = verified?.ip || null;
                                            if (fetchedIp && fetchedIp !== parsed.ip) {
                                                // IP changed: use and cache new IP
                                                ipToUse = fetchedIp;
                                                setIp(fetchedIp);
                                                try {
                                                    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ip: fetchedIp, ts: Date.now() }));
                                                } catch (e) { /* ignore write errors */ }
                                            } else {
                                                // IP same: refresh timestamp to postpone next verification
                                                try {
                                                    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ip: parsed.ip, ts: Date.now() }));
                                                } catch (e) { /* ignore write errors */ }
                                            }
                                        }
                                    } catch (verifyErr) {
                                        // Verification failed — keep cached IP
                                    }
                                }
                            }
                        }
                    }
                } catch (e) {
                    // ignore localStorage access / parse errors
                }
            }

            if (!ipToUse) {
                const ctrl = new AbortController();
                controllers.push(ctrl);
                const res = await fetch("https://api.ipify.org?format=json", { signal: ctrl.signal });
                if (!res.ok) throw new Error("Failed to fetch client IP");
                const ipData = await res.json();
                ipToUse = ipData?.ip || null;
                if (ipToUse) {
                    setIp(ipToUse);
                    try {
                        if (typeof window !== "undefined" && window.localStorage) {
                            window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ip: ipToUse, ts: Date.now() }));
                        }
                    } catch (e) {
                        // ignore localStorage write errors
                    }
                }
            }

            // Try server-side resolution first
            try {
                const response = await HttpClient.requestData("get-user-country", "POST", { ip: ipToUse });
                if (response?.success) {
                    setCountry(response?.log?.details?.countryCode || response?.log?.details?.country || null);
                    setRegion(response?.log?.details?.regionName || null);
                    setLoading(false);
                    return;
                }
            } catch (serverErr) {
                // fall through to client-side providers
            }

            // Fallback to public geo IP providers
            try {
                const ctrl2 = new AbortController();
                controllers.push(ctrl2);
                const res2 = await fetch("https://ipapi.co/json/", { signal: ctrl2.signal });
                if (res2.ok) {
                    const data = await res2.json();
                    setCountry(data?.country || data?.country_name || null);
                    setRegion(data?.region || data?.region_name || null);
                    setLoading(false);
                    return;
                }
            } catch (ipapiErr) {
                // try another fallback
            }

            // Last-resort fallback: ipinfo
            try {
                const ctrl3 = new AbortController();
                controllers.push(ctrl3);
                const res3 = await fetch("https://ipinfo.io/json?token=demo", { signal: ctrl3.signal });
                if (res3.ok) {
                    const data3 = await res3.json();
                    setCountry(data3?.country || null);
                    setRegion(data3?.region || null);
                } else {
                    throw new Error("No provider succeeded");
                }
            } catch (finalErr) {
                throw finalErr;
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
            // abort any pending requests (cleanup)
            controllers.forEach((c) => {
                try { c.abort(); } catch (e) { }
            });
        }
    }, [ip, loading]);

    // Auto fetch on mount (only once)
    useEffect(() => {
        if (autoFetch) {
            fetchCountry();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally run once

    const refresh = useCallback(() => fetchCountry({ force: true }), [fetchCountry]);

    return { country, region, ip, loading, error, refresh };
}
